import React, { useEffect, useState } from 'react'
import { Spinner, Button, Form, FormGroup, Label, Input, Alert, Container, Card, Row, CardHeader, CardTitle, CardBody, Col } from 'reactstrap';
import Switch from "react-bootstrap-switch";
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import gamesService from '../../services/api/games-service';
import contentService from "../../services/api/content-service";
import { ToastContainer, toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

const GamesSettings = (props) => {
    const projectId = new URLSearchParams(props.location.search).get('projectid');
    const [showLoader, setShowLoader] = useState(false);
    const [showSaveButton, setShowSaveButton] = useState(true);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [categories, setCategories] = useState([])
    const [otpTemplateOptions, setOtpTemplateOptions] = useState([])
    const [otpTemplatesFull, setOtpTemplatesFull] = useState([])
    const [newOtpTemplate, setNewOtpTemplate] = useState({ name: '', subject: '', html_content: '', sender_email: '' })
    const [formdetails, setFormDetails] = useState({
        project_id: projectId,
        status: false,
        category_ids: [],
        enabled_games: [],
        host_name: '',
        enable_custom_quiz: false,
        language: 'en',
        otp_template_slug: '',
    })
    const AVAILABLE_GAMES = [
        { value: 'quiz', label: 'Quiz' },
        { value: 'headline_scramble', label: 'Headline Scramble' },
        { value: 'hangman', label: 'Hangman' },
        { value: 'crossword', label: 'Crossword' },
        { value: 'poll', label: 'Poll' }
    ]

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderRadius: '12px',
            border: state.isFocused ? '2px solid #11cdef' : '1px solid #e9ecef',
            boxShadow: state.isFocused ? '0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#11cdef' : '#ced4da'
            },
            padding: '2px',
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#11cdef',
            color: 'white',
            borderRadius: '12px'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: 'white'
        })
    };

    const LANGUAGE_OPTIONS = [
        { value: 'hindi', label: 'Hindi' },
        { value: 'english', label: 'English' },
        { value: 'arabic', label: 'Arabic' },
    ];

    useEffect(() => {
        fetchGameSettings();
        fetchCategories();
        fetchOtpTemplates();
    }, [projectId]);

    // Fetches and the saved slug can resolve in either order - once both are in, make sure
    // the project's currently-saved slug always shows as selected even if it somehow isn't
    // (yet, or anymore) in the shared template list.
    useEffect(() => {
        if (!formdetails.otp_template_slug) return;
        const exists = otpTemplateOptions.some((option) => option.value === formdetails.otp_template_slug);
        if (!exists) {
            setOtpTemplateOptions((prevOptions) => [
                ...prevOptions,
                { value: formdetails.otp_template_slug, label: formdetails.otp_template_slug },
            ]);
        }
    }, [formdetails.otp_template_slug, otpTemplateOptions]);

    // Keeps the Name/Subject/HTML/Sender fields in sync with whichever slug is selected -
    // runs for every change, not just ones made through the dropdown's onChange, so a slug
    // that arrives from the saved settings (on page load) gets its content filled in too.
    useEffect(() => {
        if (!formdetails.otp_template_slug) {
            setNewOtpTemplate({ name: '', subject: '', html_content: '', sender_email: '' });
            return;
        }
        const existing = otpTemplatesFull.find((template) => template.slug === formdetails.otp_template_slug);
        setNewOtpTemplate({
            name: existing?.name || '',
            subject: existing?.subject || '',
            html_content: existing?.html_content || '',
            sender_email: existing?.sender_email || '',
        });
    }, [formdetails.otp_template_slug, otpTemplatesFull]);

    const fetchGameSettings = async () => {
        try {
            const gamesDetails = await gamesService.getGamesSettings({ project_id: projectId });
            if (gamesDetails.status && gamesDetails.data) {
                setFormDetails((prevDetails) => ({
                    ...prevDetails,
                    status: gamesDetails.data.status,
                    category_ids: gamesDetails.data.category_ids || [],
                    enabled_games: gamesDetails.data.enabled_games || [],
                    host_name: gamesDetails.data.host_name || '',
                    enable_custom_quiz: gamesDetails.data.enable_custom_quiz || false,
                    language: gamesDetails.data.language || 'en',
                    otp_template_slug: gamesDetails.data.otp_template_slug || '',
                }));
            }
        } catch (error) {
            setAlert({
                show: true,
                type: 'danger',
                message: 'Failed to fetch game settings. Please try again later.',
            });
        }
    };

    const fetchCategories = async () => {
        try {
            const categoryData = await contentService.getCategories(projectId);
            if (categoryData.status && categoryData.data?.length) {
                const categoryArray = categoryData.data.flatMap((category) => {
                    const subCategories = category.sub_categories?.map((subCategory) => ({
                        value: subCategory._id.toString(),
                        label: subCategory.name,
                    })) || [];
                    return [
                        { value: category._id.toString(), label: category.name },
                        ...subCategories,
                    ];
                });
                setCategories(categoryArray);
            }
        } catch (error) {
            setAlert({
                show: true,
                type: 'danger',
                message: 'Failed to fetch categories. Please try again later.',
            });
        }
    };

    const fetchOtpTemplates = async () => {
        try {
            const templatesResponse = await gamesService.listOtpTemplates();
            if (templatesResponse.status && Array.isArray(templatesResponse.data)) {
                setOtpTemplatesFull(templatesResponse.data);
                setOtpTemplateOptions(
                    templatesResponse.data.map((template) => ({
                        value: template.slug,
                        label: template.name,
                    }))
                );
            }
        } catch (error) {
            // Non-fatal - the dropdown just falls back to "create new" only.
            console.error('Failed to fetch OTP templates:', error.message || error);
        }
    };

    const handleOtpTemplateChange = (selectedOption) => {
        if (!selectedOption) {
            setFormDetails((prevDetails) => ({ ...prevDetails, otp_template_slug: '' }));
            return;
        }
        if (selectedOption.__isNew__) {
            setOtpTemplateOptions((prevOptions) => [
                ...prevOptions,
                { value: selectedOption.value, label: selectedOption.value },
            ]);
        }
        setFormDetails((prevDetails) => ({ ...prevDetails, otp_template_slug: selectedOption.value }));
    };

    const handleNewOtpTemplateChange = (fieldname, value) => {
        setNewOtpTemplate((prevTemplate) => ({ ...prevTemplate, [fieldname]: value }));
    };

    const handleSwitchChange = (fieldname, fieldvalue) => {
        setFormDetails({
            ...formdetails,
            [fieldname]: fieldvalue,
        });
    }

    const handleMultiSelectChange = (fieldname, selectedOptions) => {
        const validOptions = Array.isArray(selectedOptions) ? selectedOptions : [];
        setFormDetails((prevDetails) => ({
            ...prevDetails,
            [fieldname]: validOptions.map((option) => option.value) || [],
        }));
    };

    const handleInputChange = (fieldname, value) => {
        setFormDetails({
            ...formdetails,
            [fieldname]: value,
        });
    };

    const handleSelectChange = (fieldname, selectedOption) => {
        setFormDetails((prevDetails) => ({
            ...prevDetails,
            [fieldname]: selectedOption ? selectedOption.value : '',
        }));
    };

    const validateForm = async () => {
        if (formdetails.status && formdetails.category_ids.length === 0) {
            toast.error("Please select at least one category when games are enabled!")
            return;
        }

        if (formdetails.otp_template_slug) {
            if (!newOtpTemplate.subject || !newOtpTemplate.html_content) {
                toast.error("Subject and HTML Content are required for the OTP template!")
                return;
            }
        }

        setShowLoader(true)
        setShowSaveButton(false)
        try {
            if (formdetails.otp_template_slug) {
                const createTemplateResponse = await gamesService.createOtpTemplate({
                    slug: formdetails.otp_template_slug,
                    name: newOtpTemplate.name,
                    subject: newOtpTemplate.subject,
                    html_content: newOtpTemplate.html_content,
                    sender_email: newOtpTemplate.sender_email,
                });
                if (!createTemplateResponse.status) {
                    setAlert({
                        show: true,
                        type: 'danger',
                        message: 'Failed to save the OTP template. Please try again.'
                    });
                    setShowLoader(false);
                    setShowSaveButton(true);
                    return;
                }
            }
            let saveGamesSettings = await gamesService.saveGamesSettings(formdetails);
            if (!saveGamesSettings.status && saveGamesSettings.error.errorCode === 403) {
                // context.setIsAuthorized(context);
            }
            else if (saveGamesSettings.status) {
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Games settings were saved successfully!'
                });
            } else {
                setAlert({
                    show: true,
                    type: 'danger',
                    message: 'Games settings could not be saved, Please try again.'
                });
            }
        } catch (error) {
            setAlert({
                show: true,
                type: 'danger',
                message: 'An error occurred while saving the settings. Please try again later.'
            });
        } finally {
            setShowLoader(false);
            setShowSaveButton(true);
        }

        setTimeout(() => {
            setAlert({ show: false, type: '', message: '' });
        }, 3000);
    }

    return (
        <div className='content'>
            <Container>
                <Row>
                    <Col className="ml-auto mr-auto" md="12">
                        <Card 
                            className="card-signup text-center shadow-lg" 
                            style={{
                                borderRadius: '20px',
                                border: 'none',
                                backgroundColor: '#f8f9fe'
                            }}
                        >
                            <CardHeader 
                                className=" text-black" 
                                style={{
                                    borderTopLeftRadius: '20px',
                                    borderTopRightRadius: '20px',
                                    padding: '1rem'
                                }}
                            >
                                <CardTitle tag="h3" className="text-black mb-0">
                                    Games Configuration
                                </CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Form
                                    className="form-horizontal"
                                    action=""
                                    id="projectSetting"
                                    method="POST"
                                >
                                    <Row className="mb-4 align-items-center">
                                        <Label sm="3" className="text-dark font-weight-bold">Enable Games</Label>
                                        <Col sm="9" className="d-flex justify-content-start">
                                            <FormGroup className="mb-0">
                                                <Switch
                                                    name="enable_games"
                                                    offColor="success"
                                                    offText={<i className="nc-icon nc-simple-remove" />}
                                                    onColor="success"
                                                    onText={<i className="nc-icon nc-check-2" />}
                                                    value={formdetails.status}
                                                    onChange={(value, enabled) =>
                                                        handleSwitchChange("status", enabled)
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {formdetails.status && (
                                        <>
                                            <Row className="mb-4 align-items-center">
                                                <Label sm="3" className="text-dark font-weight-bold">Enable Custom Quiz</Label>
                                                <Col sm="9" className="d-flex justify-content-start">
                                                    <FormGroup className="mb-0">
                                                        <Switch
                                                            name="enable_custom_quiz"
                                                            offColor="success"
                                                            offText={<i className="nc-icon nc-simple-remove" />}
                                                            onColor="success"
                                                            onText={<i className="nc-icon nc-check-2" />}
                                                            value={formdetails.enable_custom_quiz}
                                                            onChange={(value, enabled) =>
                                                                handleSwitchChange("enable_custom_quiz", enabled)
                                                            }
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row className="mb-4">
                                                <Label sm="3" className="text-dark font-weight-bold">Select Categories</Label>
                                                <Col sm="9">
                                                    <FormGroup>
                                                        <Select
                                                            isMulti
                                                            options={categories}
                                                            styles={customSelectStyles}
                                                            value={categories.filter(category =>
                                                                formdetails.category_ids.includes(category.value)
                                                            )}
                                                            onChange={(selectedOption) => handleMultiSelectChange('category_ids', selectedOption)}
                                                            placeholder="Select categories"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                    <Row className="mb-4">
                                        <Label sm="3" className="text-dark font-weight-bold">Select Language</Label>
                                        <Col sm="9">
                                            <FormGroup>
                                                <Select
                                                    options={LANGUAGE_OPTIONS}
                                                    styles={customSelectStyles}
                                                    value={LANGUAGE_OPTIONS.find(lang => lang.value === formdetails.language) || null}
                                                    onChange={(selectedOption) => handleSelectChange('language', selectedOption)}
                                                    placeholder="Select language"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                            <Row className="mb-4">
                                                <Label sm="3" className="text-dark font-weight-bold">Select Games</Label>
                                                <Col sm="9">
                                                    <FormGroup>
                                                        <Select
                                                            isMulti
                                                            options={AVAILABLE_GAMES}
                                                            styles={customSelectStyles}
                                                            value={AVAILABLE_GAMES.filter(game =>
                                                                formdetails.enabled_games.includes(game.value)
                                                            )}
                                                            onChange={(selectedOption) => handleMultiSelectChange('enabled_games', selectedOption)}
                                                            placeholder="Select Games"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Row className="mb-4">
                                                <Label sm="3" className="text-dark font-weight-bold">Host Name</Label>
                                                <Col sm="9">
                                                    <FormGroup>
                                                        <Input
                                                            type="text"
                                                            name="host_name"
                                                            value={formdetails.host_name}
                                                            onChange={(e) => handleInputChange('host_name', e.target.value)}
                                                            placeholder="Enter host name (e.g., example.sortdgames.com)"
                                                            style={{
                                                                borderRadius: '12px',
                                                                border: '1px solid #e9ecef',
                                                                padding: '10px 15px',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Row className="mb-4">
                                                <Label sm="3" className="text-dark font-weight-bold">OTP Email Template</Label>
                                                <Col sm="9">
                                                    <FormGroup>
                                                        <CreatableSelect
                                                            options={otpTemplateOptions}
                                                            styles={customSelectStyles}
                                                            value={otpTemplateOptions.find(option => option.value === formdetails.otp_template_slug) || null}
                                                            onChange={handleOtpTemplateChange}
                                                            isClearable
                                                            placeholder="Select an existing template or type a new slug to create one"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            {formdetails.otp_template_slug && (
                                                <>
                                                    <Row className="mb-4">
                                                        <Label sm="3" className="text-dark font-weight-bold">Template Name</Label>
                                                        <Col sm="9">
                                                            <FormGroup>
                                                                <Input
                                                                    type="text"
                                                                    value={newOtpTemplate.name}
                                                                    onChange={(e) => handleNewOtpTemplateChange('name', e.target.value)}
                                                                    placeholder="Optional - display name shown in this dropdown, defaults to the slug"
                                                                    style={{
                                                                        borderRadius: '12px',
                                                                        border: '1px solid #e9ecef',
                                                                        padding: '10px 15px',
                                                                        transition: 'all 0.3s ease'
                                                                    }}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-4">
                                                        <Label sm="3" className="text-dark font-weight-bold">Subject</Label>
                                                        <Col sm="9">
                                                            <FormGroup>
                                                                <Input
                                                                    type="text"
                                                                    value={newOtpTemplate.subject}
                                                                    onChange={(e) => handleNewOtpTemplateChange('subject', e.target.value)}
                                                                    placeholder="Email subject line"
                                                                    style={{
                                                                        borderRadius: '12px',
                                                                        border: '1px solid #e9ecef',
                                                                        padding: '10px 15px',
                                                                        transition: 'all 0.3s ease'
                                                                    }}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-4">
                                                        <Label sm="3" className="text-dark font-weight-bold">HTML Content</Label>
                                                        <Col sm="9">
                                                            <FormGroup>
                                                                <Input
                                                                    type="textarea"
                                                                    rows="8"
                                                                    value={newOtpTemplate.html_content}
                                                                    onChange={(e) => handleNewOtpTemplateChange('html_content', e.target.value)}
                                                                    placeholder="Full email HTML - use {{otp}} where the code should appear"
                                                                    style={{
                                                                        borderRadius: '12px',
                                                                        border: '1px solid #e9ecef',
                                                                        padding: '10px 15px',
                                                                        transition: 'all 0.3s ease',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '0.85rem'
                                                                    }}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-4">
                                                        <Label sm="3" className="text-dark font-weight-bold">Sender Email</Label>
                                                        <Col sm="9">
                                                            <FormGroup>
                                                                <Input
                                                                    type="email"
                                                                    value={newOtpTemplate.sender_email}
                                                                    onChange={(e) => handleNewOtpTemplateChange('sender_email', e.target.value)}
                                                                    placeholder="Optional - leave blank to use the default sender"
                                                                    style={{
                                                                        borderRadius: '12px',
                                                                        border: '1px solid #e9ecef',
                                                                        padding: '10px 15px',
                                                                        transition: 'all 0.3s ease'
                                                                    }}
                                                                />
                                                                <small className="text-muted d-block mt-1">
                                                                    This address must already be verified in AWS SES, or sending will fail.
                                                                </small>
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </>
                                            )}
                                        </>
                                    )}

                                    {showSaveButton && (
                                        <Button
                                            className="btn-round shadow-sm"
                                            color="primary"
                                            type="button"
                                            onClick={validateForm}
                                            style={{
                                                borderRadius: '30px',
                                                padding: '10px 25px',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Save Settings
                                        </Button>
                                    )}

                                    {showLoader && (
                                        <div className="text-center mt-3">
                                            <Spinner color="primary" />
                                        </div>
                                    )}

                                    {alert.show && (
                                        <Alert 
                                            color={alert.type} 
                                            className="mt-3"
                                            style={{ borderRadius: '10px' }}
                                        >
                                            {alert.message}
                                        </Alert>
                                    )}
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer 
                autoClose={3000} 
                position='bottom-right' 
                newestOnTop
                closeOnClick
                pauseOnHover
            />
        </div>
    )
}

export default GamesSettings